import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle as faCircleSolid } from '@fortawesome/free-solid-svg-icons';
import { faCircle as faCircleRegular } from '@fortawesome/free-regular-svg-icons';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { useFieldContext } from '@/lib/form-context';
import { Field, FieldContent, FieldError, FieldLabel } from '@/components/ui/field';
import { filterFieldErrors } from '../utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { type VariantProps } from 'class-variance-authority';
import { schemaFieldVariants } from '../constants';

interface PasswordFieldProps extends VariantProps<typeof schemaFieldVariants> {
  label: string;
  autoComplete?: string;
  showRequirements?: boolean;
  serverError?: string | null;
  onServerErrorClear?: () => void;
}

export const PasswordField = ({
  label,
  autoComplete = 'current-password',
  showRequirements = false,
  serverError,
  onServerErrorClear,
  variant: _variant, // PasswordField is always vertical; variant reserved for future use
}: PasswordFieldProps) => {
  const field = useFieldContext<string>();
  const [show, setShow] = useState(false);

  const errors = field.state.meta.errors;
  const hasError = (field.state.meta.isTouched && errors.length > 0) || !!serverError;

  const value = field.state.value ?? '';
  const hasLength = value.length >= 8;
  const withinMax = value.length === 0 || value.length <= 64;
  const hasUpper = /[A-Z]/.test(value);
  const hasLower = /[a-z]/.test(value);
  const hasNumber = /\d/.test(value);
  const hasSymbol = /[!@#$%^&*(),.?":;{}|<>\/\-_\\]/.test(value);

  return (
    <Field orientation="vertical" data-invalid={hasError || undefined}>
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <FieldContent>
        <div className="relative">
          <Input
            id={field.name}
            type={show ? 'text' : 'password'}
            autoComplete={autoComplete}
            value={value}
            onBlur={field.handleBlur}
            onChange={(e) => {
              onServerErrorClear?.();
              field.handleChange(e.target.value);
            }}
            aria-describedby={showRequirements ? `${field.name}-requirements` : undefined}
            className="pr-10"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="absolute top-1/2 right-1 -translate-y-1/2"
            aria-label={show ? 'Hide password' : 'Show password'}
            onClick={() => setShow((v) => !v)}
          >
            {show ? <EyeOffIcon /> : <EyeIcon />}
          </Button>
        </div>

        {showRequirements && (
          <ul
            id={`${field.name}-requirements`}
            aria-label="Password requirements"
            className="mt-1 space-y-1 text-sm"
          >
            <li className={cn(hasLength ? 'text-foreground' : 'text-muted-foreground')}>
              <FontAwesomeIcon
                aria-hidden="true"
                icon={hasLength ? faCircleSolid : faCircleRegular}
              />{' '}
              At least 8 characters
            </li>
            <li className={cn(withinMax ? 'text-foreground' : 'text-muted-foreground')}>
              <FontAwesomeIcon
                aria-hidden="true"
                icon={withinMax ? faCircleSolid : faCircleRegular}
              />{' '}
              No more than 64 characters
            </li>
            <li className={cn(hasUpper ? 'text-foreground' : 'text-muted-foreground')}>
              <FontAwesomeIcon
                aria-hidden="true"
                icon={hasUpper ? faCircleSolid : faCircleRegular}
              />{' '}
              One uppercase letter
            </li>
            <li className={cn(hasLower ? 'text-foreground' : 'text-muted-foreground')}>
              <FontAwesomeIcon
                aria-hidden="true"
                icon={hasLower ? faCircleSolid : faCircleRegular}
              />{' '}
              One lowercase letter
            </li>
            <li className={cn(hasNumber ? 'text-foreground' : 'text-muted-foreground')}>
              <FontAwesomeIcon
                aria-hidden="true"
                icon={hasNumber ? faCircleSolid : faCircleRegular}
              />{' '}
              One number
            </li>
            <li className={cn(hasSymbol ? 'text-foreground' : 'text-muted-foreground')}>
              <FontAwesomeIcon
                aria-hidden="true"
                icon={hasSymbol ? faCircleSolid : faCircleRegular}
              />{' '}
              One special character
            </li>
          </ul>
        )}

        {hasError && (
          <FieldError
            errors={[
              ...filterFieldErrors(errors),
              ...(serverError ? [{ message: serverError }] : []),
            ]}
          />
        )}
      </FieldContent>
    </Field>
  );
};
